/** @format */

import apis from "../../utils/apis";
import {
  retryApi,
  pollUntilTerminal,
  MAX_STAGE_ATTEMPTS,
} from "./asyncPollingUtils";

/**
 * Fetch Skill & Role Disruption data using async trigger + status polling pattern.
 *
 * Flow:
 *  1. POST /get-skillAndRoleDisruption-trigger  → kicks off the background job.
 *  2. If the backend completes synchronously the result is returned immediately.
 *  3. Otherwise we poll GET /get-skillAndRoleDisruption-status?jobId=<id> until
 *     the job reaches a terminal state ("completed" / "failed") or we exhaust
 *     the polling budget.
 *
 * @param {string} skillName - The skill name to fetch disruption data for (e.g. "React")
 * @returns {Promise<{statusCode: number, body: any}>} API response with skill and role disruption data
 */
export const fetchSkillAndRoleDisruption = async (skillName) => {
  for (let attempt = 1; attempt <= MAX_STAGE_ATTEMPTS; attempt++) {
    try {
      // Step 1: Trigger the async job
      const triggerRes = await retryApi(() =>
        apis.POST({
          endpoint: "/get-skillAndRoleDisruption-trigger",
          payload: { skill: skillName },
          options: { type: "json" },
        })
      );

      if (!triggerRes.data || triggerRes.data.error) {
        throw new Error(
          "Failed to trigger Skill & Role Disruption processing"
        );
      }

      // If the backend completed synchronously (no polling needed)
      if (triggerRes.data.status !== "pending") {
        return {
          statusCode: triggerRes.status,
          body: triggerRes.data.result,
        };
      }

      // Step 2: Async path — poll status until completed / failed
      const jobId = triggerRes.data.jobId;
      if (!jobId) {
        throw new Error(
          "No jobId received from Skill & Role Disruption trigger."
        );
      }

      const statusData = await pollUntilTerminal({
        statusEndpoint: `/get-skillAndRoleDisruption-status?jobId=${jobId}`,
        statusField: "status",
        timeoutError:
          "Skill & Role Disruption processing timed out while waiting for completion.",
      });

      if (!statusData) {
        throw new Error(
          "Skill & Role Disruption processing timed out while waiting for completion."
        );
      }

      return {
        statusCode: 200,
        body: statusData.result,
      };
    } catch (err) {
      console.error(
        `❌ Skill & Role Disruption processing failed (attempt ${attempt}/${MAX_STAGE_ATTEMPTS}):`,
        err
      );
      const msg = String(err?.message ?? err ?? "");
      const isTimeout = msg.includes("timed out");
      if (isTimeout && attempt < MAX_STAGE_ATTEMPTS) continue;
      throw new Error(
        err?.response?.data?.body?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load Skill & Role Disruption data"
      );
    }
  }
};
